/**
 * Note:
 * This file should be synced with the corresponding layout in Contentful CMS.
 */
import {
  params,
  arrayOf,
  string,
  fragment,
  shape,
  number,
  bool,
  extend,
  alias,
  customType,
} from '@thd-nucleus/data-sources';
import { Hero } from '@thd-olt-component-react/hero';
import { Spotlight } from '@thd-olt-component-react/spotlight';
import { ShopByCategory } from '@thd-olt-component-react/shop-by-category';
import { VisualNavigation } from '@thd-olt-component-react/visual-navigation';
//   import { Gallery } from '../TODORemove/gallery';
import { ComponentSelector } from '@thd-olt-component-react/component-selector';

let HeroFlattenImage = {};
HeroFlattenImage.component = {
  ...Hero.component,
  mobileImage: shape({
    url: string()
  }),
};

HeroFlattenImage.dataModel = {
  id: string(),
  image: shape({
    contentType: string(),
    description: string(),
    id: string(),
    publishDate: string(),
    title: string(),
    url: string(),
  }),
  __typename: string(),
  link: string(),
  mobileImage: shape({
    contentType: string(),
    description: string(),
    id: string(),
    publishDate: string(),
    title: string(),
    url: string(),
  }),
  title: string(),
};

ComponentSelector.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['ComponentVariations']).isRequired(),
  }).shape({
    ComponentVariations: fragment().shape({
      id: string(),
      location: string(),
      defaultComponent: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Spotlight.dataModel.component,
          HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
        })
      ),
      variations: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Spotlight.dataModel.component,
          HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
        })
      ),
    }),
    ...Hero.dataModel.component,
    ...Spotlight.dataModel.component,
    HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
  }),
};

let Recommendations = {};
Recommendations.dataModel = {
  id: string(),
  __typename: string(),
  customRenderId: string(),
  // schemaData: string()
};

let SponsoredBanner = {};
SponsoredBanner.dataModel = {
  id: string(),
  __typename: string(),
  customRenderId: string(),
  schema: string()
};

let PersonalizedHomeServices = {};
PersonalizedHomeServices.dataModel = {
  id: string(),
  __typename: string(),
  customRenderId: string(),
};

let RecentOrderHistory = {};
RecentOrderHistory.dataModel = {
  id: string(),
  customRenderId: string(),
  __typename: string()
};

let PrivateLabelCreditCard = {};
PrivateLabelCreditCard.dataModel = {
  id: string(),
  customRenderId: string(),
  __typename: string()
};

let Tile = {};
Tile.dataModel = {
  component: params({
    id: string().isRequired(),
  }).shape({
    Tile: fragment().shape({
      link: string(),
      id: string(),
      __typename: string(),
      title: string(),
      description: string(),
      cta: string(),
      image: shape({
        description: string(),
        url: string()
      })
    })
  })
};

let SectionDM = {};
SectionDM.dataModel = {
  id: string(),
  title: string(),
  customRenderId: string(),
  isCarousel: bool(),
  components: arrayOf(
    shape({
      ...Hero.dataModel.component,
      ...Spotlight.dataModel.component,
      ...ComponentSelector.dataModel.component,
      ...Tile.dataModel.component,
      ...ShopByCategory.dataModel.component,
      ...VisualNavigation.dataModel.component,
      HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
    })
  ),
  __typename: string(),
};

export const authB2cDataModel = extend({
  layouts: params({
    slug: string().isRequired(),
  }).shape({
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: shape({
          id: string(),
          customRenderId: string(),
          isCarousel: bool(),
          components: arrayOf(shape({
            ...Hero.dataModel.component,
            ...ComponentSelector.dataModel.component,
            HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
          }))
        }),
        welcomeMessage: string(),
        componentOrder: shape({
          ...Spotlight.dataModel.component,
          Recommendations: fragment().shape(Recommendations.dataModel),
          Section: fragment().shape(SectionDM.dataModel),
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
        }),
      }),
    }),
    seo: shape({
      title: string(),
      description: string(),
      facebookAdmins: string(),
      siteName: string(),
      type: string(),
      canonicalURL: string(),
      keywords: arrayOf(string()),
      no_index: bool(),
      no_follow: bool()
    })
  }),
  PrivateLabelCreditCard,
  RecentOrderHistory,
  Spotlight,
  ShopByCategory,
  VisualNavigation
});

export const guestB2cDataModel = extend({
  layouts: params({
    slug: string().isRequired(),
  }).shape({
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: shape({
          id: string(),
          customRenderId: string(),
          isCarousel: bool(),
          components: arrayOf(shape({
            ...Hero.dataModel.component,
            ...ComponentSelector.dataModel.component,
            HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
          }))
        }),
        componentOrder: shape({
          ...Spotlight.dataModel.component,
          Recommendations: fragment().shape(Recommendations.dataModel),
          Section: fragment().shape(SectionDM.dataModel),
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
        }),
      }),
    }),
    seo: shape({
      title: string(),
      description: string(),
      facebookAdmins: string(),
      siteName: string(),
      type: string(),
      canonicalURL: string(),
      keywords: arrayOf(string()),
      no_index: bool(),
      no_follow: bool()
    })
  }),
  PrivateLabelCreditCard,
  RecentOrderHistory,
  Spotlight,
  ShopByCategory,
  VisualNavigation
});

export const homepagePreviewByIDDataModel = extend({
  layout: params({
    layoutClass: customType('LayoutClass').enum(['PersonalizedHomePage']),
    id: string().isRequired(),
  }).shape({
    PersonalizedHomePage: fragment().shape({
      // you need this here too because the query is shaped at a lower level
      // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
      heroCarousel: shape({
        id: string(),
        customRenderId: string(),
        isCarousel: bool(),
        components: arrayOf(shape({
          ...Spotlight.dataModel.component,
          ...Hero.dataModel.component,
          ...ComponentSelector.dataModel.component,
          HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
        })),
      }),
      welcomeMessage: string(),
      componentOrder: shape({
        ...Spotlight.dataModel.component,
        Recommendations: fragment().shape(Recommendations.dataModel),
        Section: fragment().shape(SectionDM.dataModel),
        SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
      }),
    }),
  }),
  PrivateLabelCreditCard,
  RecentOrderHistory,
  Spotlight,
  ShopByCategory,
  VisualNavigation
});

export const authB2bDataModel = extend({
  layouts: params({
    slug: string().isRequired(),
  }).shape({
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: shape({
          id: string(),
          customRenderId: string(),
          isCarousel: bool(),
          components: arrayOf(shape({
            ...Hero.dataModel.component,
            ...ComponentSelector.dataModel.component,
            HeroFlattenImage: fragment().shape(HeroFlattenImage.dataModel)
          })),
        }),
        welcomeMessage: string(),
        componentOrder: shape({
          ...Spotlight.dataModel.component,
          Recommendations: fragment().shape(Recommendations.dataModel),
          Section: fragment().shape(SectionDM.dataModel),
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
        }),
      }),
    }),
    seo: shape({
      title: string(),
      description: string(),
      facebookAdmins: string(),
      siteName: string(),
      type: string(),
      canonicalURL: string(),
      keywords: arrayOf(string()),
      no_index: bool(),
      no_follow: bool()
    })
  }),
  PrivateLabelCreditCard,
  RecentOrderHistory,
  Spotlight,
  ShopByCategory,
  VisualNavigation
});
